import { SUPPORTED_LANGUAGES } from '@ophelos/shared/utils/languages';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { changeLanguage as changeI18nLanguage } from '@/lib/i18n';
import { useConfiguration } from '@/contexts/ConfigurationContext';

// Check if we're in preview/demo mode
function isPreviewOrDemoMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const stored = sessionStorage.getItem('hub_preview_mode') || sessionStorage.getItem('hub_demo_mode');
    if (stored === 'true') return true;
  } catch {
    // Ignore errors
  }
  const urlPreview = new URLSearchParams(window.location.search).get('preview') === 'true';
  const urlDemo = new URLSearchParams(window.location.search).get('demo') === 'true';
  return urlPreview || urlDemo;
}

/**
 * Codes are widened to `string`: they are compared against `availableLanguages`
 * from brand config, which is runtime data and not known to the type system.
 */
export const languages: ReadonlyArray<{ code: string; name: string; flag: string }> = SUPPORTED_LANGUAGES.map(
  ({ bcp47, nativeName, flag }) => ({ code: bcp47, name: nativeName, flag })
);

export type LanguageCode = string;
type Language = (typeof languages)[number];

const LANGUAGE_STORAGE_KEY = 'customer-portal-language';

interface LanguageContextValue {
  currentLanguage: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  /** Languages the brand enabled — what the switcher offers, not every supported language. */
  languages: readonly Language[];
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

/**
 * Language Provider for Customer Portal
 *
 * Manages language selection across the entire app and persists it to localStorage.
 * Uses the config's default language from the theme configuration.
 *
 * Usage:
 * ```tsx
 * import { LanguageProvider } from '@/contexts/LanguageContext';
 *
 * <LanguageProvider>
 *   <App />
 * </LanguageProvider>
 * ```
 */
export function LanguageProvider({ children }: LanguageProviderProps) {
  const { config, isLoading } = useConfiguration();

  // Get default language from config, falling back to 'en-GB' if not available
  const configDefaultLanguage = (config.ui.language as LanguageCode) || 'en-GB';

  const [currentLanguage, setCurrentLanguageState] = useState<LanguageCode>(() => {
    // In preview/demo mode, check URL parameter first
    if (typeof window !== 'undefined') {
      const isPreview = isPreviewOrDemoMode();
      if (isPreview) {
        const urlLang = new URLSearchParams(window.location.search).get('lang');
        if (urlLang && languages.some((lang) => lang.code === urlLang)) {
          return urlLang as LanguageCode;
        }
      }

      // Try to load from localStorage on mount (for non-preview mode)
      try {
        const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (stored && languages.some((lang) => lang.code === stored)) {
          return stored as LanguageCode;
        }
      } catch {
        // localStorage can throw in private browsing mode
      }
    }
    return configDefaultLanguage;
  });

  // Sync language with config when config changes (e.g., after login loads account-specific config)
  // Only update if user hasn't explicitly set a language preference in localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isPreview = isPreviewOrDemoMode();

    // In preview mode, language is controlled by URL params or messages, not config
    if (isPreview) return;

    // Check if user has an explicit preference in localStorage
    try {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored && languages.some((lang) => lang.code === stored)) {
        // User has explicit preference, don't override with config
        return;
      }
    } catch {
      // localStorage can throw in private browsing mode
    }

    // No explicit preference, sync with config's default language
    const configLang = config.ui.language as LanguageCode;
    if (configLang && languages.some((lang) => lang.code === configLang)) {
      setCurrentLanguageState(configLang);
    }
  }, [config.ui.language]);

  // Reconcile the active language when the brand disables it (e.g. a stored
  // en-GB preference opening a brand that only enables fr-FR): fall back to the
  // brand default if enabled, otherwise the first enabled language.
  // Gated on !isLoading: the pre-load DEFAULT config omits some languages, so
  // reconciling against it would clobber a valid stored preference (e.g. fr-FR)
  // before the real brand config arrives.
  useEffect(() => {
    if (isLoading) return;

    const available = config.ui.availableLanguages;
    if (!available?.length) return;

    const enabled = languages.filter((lang) => available.includes(lang.code)).map((lang) => lang.code);

    if (enabled.length === 0 || enabled.includes(currentLanguage)) return;

    const configDefault = config.ui.language as string;
    setCurrentLanguageState(enabled.includes(configDefault) ? configDefault : enabled[0]);
  }, [isLoading, config.ui.availableLanguages, config.ui.language, currentLanguage]);

  // In preview/demo mode, listen for URL parameter changes and LANGUAGE_UPDATE messages
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isPreview = isPreviewOrDemoMode();

    // Listen for LANGUAGE_UPDATE messages in preview mode
    if (isPreview) {
      const handleMessage = (event: MessageEvent) => {
        // Validate origin - allow localhost on any port for development
        const isLocalhost =
          event.origin.startsWith('http://localhost:') ||
          event.origin.startsWith('http://127.0.0.1:') ||
          event.origin.startsWith('https://hub.localhost') ||
          event.origin.startsWith('https://connect.localhost');

        if (!isLocalhost) {
          return;
        }

        if (event.data?.type === 'LANGUAGE_UPDATE' && event.data?.payload?.language) {
          const newLanguage = event.data.payload.language as LanguageCode;
          if (languages.some((lang) => lang.code === newLanguage)) {
            setCurrentLanguageState(newLanguage);
          }
        }
      };

      window.addEventListener('message', handleMessage);
      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }
  }, []);

  // Keep i18n in sync with the active language. Persistence is deliberately NOT
  // done here: this effect also fires for the pt-PT default rendered before the
  // async config load resolves, and writing that to localStorage makes the
  // sync-from-config effect treat it as an explicit user preference and refuse
  // to apply the brand's real default. Only setLanguage (an explicit user
  // action) persists.
  useEffect(() => {
    changeI18nLanguage(currentLanguage);
  }, [currentLanguage]);

  const setLanguage = (language: LanguageCode) => {
    setCurrentLanguageState(language);
    if (typeof window !== 'undefined' && !isPreviewOrDemoMode()) {
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      } catch {
        // localStorage can throw in private browsing mode
      }
    }
  };

  // The switcher only offers languages the brand enabled; fall back to the full
  // list when none are configured (or none match) so it is never empty.
  const configuredLanguages = languages.filter((lang) => config.ui.availableLanguages?.includes(lang.code));
  const enabledLanguages = configuredLanguages.length > 0 ? configuredLanguages : languages;

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, languages: enabledLanguages }}>{children}</LanguageContext.Provider>
  );
}

/**
 * Hook to access language context
 *
 * Returns:
 * - currentLanguage: Currently selected language code
 * - setLanguage: Function to change the language
 * - languages: Array of all available languages
 *
 * @example
 * ```tsx
 * const { currentLanguage, setLanguage } = useLanguage();
 *
 * <Select value={currentLanguage} onValueChange={setLanguage}>
 *   ...
 * </Select>
 * ```
 */
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
